import { useGetSelectionFormData } from "@/api/cms";
import {
  useGetGlobalMode,
  useGetSectionById,
  useGetSectionMetadata,
  useGetSectionSelections,
  useSetSectionMetadata,
} from "@/api/section";
import { ManageLayoutFullMenu } from "@/components/cms/selection/LayoutSelector";
import { SectionImportImageForm } from "@/components/cms/selection/SectionImportImageForm";
import SectionSelectedImages from "@/components/cms/selection/SectionSelectedImages";
import StyledDrawer from "@/components/StyledDrawer";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { error, success, useToast } from "@/hooks/use-toast";
import { getMainLanguage } from "@/i18n";
import CMSLayout from "@/layout/CMSLayout";
import {
  SectionDescriptionVisibility,
  SectionMetadata,
  sectionMetadataSchema,
} from "@/lib/cms/section-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo, useState } from "react";
import { Control, Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AddSelectionToSection from "./add-selection-to-section";

const SectionArtworks = () => {
  const navigate = useNavigate();

  const { id } = useParams();
  const { t } = useTranslation();
  const { data: selectedImages } = useGetSectionSelections(id);
  const { data: sectionData } = useGetSectionById(id);
  const { data: sectionMetadata } = useGetSectionMetadata(id);
  const { mutateAsync: setSectionMetadata } = useSetSectionMetadata(id);
  const { data: selectionPageData } = useGetSelectionFormData();
  const { data: globalModeData } = useGetGlobalMode();

  const { toast } = useToast();

  const [artworkSelectionIsDirty, setArtworkSelectionIsDirty] = useState(false);

  const [isImportImageModalOpen, setIsImportImageModalOpen] = useState(false);

  const defaultValues: SectionMetadata = useMemo(
    () => ({
      layout: {
        numberOfColumns: sectionMetadata?.layout?.numberOfColumns ?? 2,
        alignment: sectionMetadata?.layout?.alignment ?? "CENTER",
        captionAlignment: sectionMetadata?.layout?.captionAlignment ?? "LEFT",
      },
      visibleTitles: sectionMetadata?.visibleTitles ?? false,
      visibleAuthors: sectionMetadata?.visibleAuthors ?? false,
      visibleDates: sectionMetadata?.visibleDates ?? false,
      visibleTechniqueAndSupport:
        sectionMetadata?.visibleTechniqueAndSupport ?? false,
      visibleDimensions: sectionMetadata?.visibleDimensions ?? false,
      visibleSignature: sectionMetadata?.visibleSignature ?? false,
      visibleBackText: sectionMetadata?.visibleBackText ?? false,
      visibleCurrentCollection:
        sectionMetadata?.visibleCurrentCollection ?? false,
      visibleRegisterNumber: sectionMetadata?.visibleRegisterNumber ?? false,
      visibleCustomField: sectionMetadata?.visibleCustomField ?? false,
    }),
    [sectionMetadata],
  );

  const form = useForm<SectionMetadata>({
    resolver: zodResolver(sectionMetadataSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    if (sectionMetadata) {
      form.reset(defaultValues);
    }
  }, [sectionMetadata, defaultValues, form]);

  const onSubmit = (data: SectionMetadata) => {
    setSectionMetadata(
      { sectionMetadata: data },
      {
        onError: (e) => {
          console.error("Error", e);
          toast(
            error({
              description: t("cms.selection.manageLayout.error.missingLayout"),
            }),
          );
        },
        onSuccess: () => {
          toast(
            success({
              description: t("cms.selection.manageLayout.success.updateLayout"),
            }),
          );
          // If in global mode, redirect to selection page, otherwise to section page
          const isGlobalMode = globalModeData?.isGlobalMode || false;
          if (isGlobalMode) {
            navigate("/cms/selection");
          } else {
            navigate("/cms/selection/section/" + id);
          }
        },
      },
    );
  };

  return (
    <CMSLayout
      formIds={["sectionArtworks"]}
      pageName={t("cms.selection.section.artworks.title")}
      isValid={!artworkSelectionIsDirty}
      breadcrumbs={(() => {
        const isGlobalMode = globalModeData?.isGlobalMode || false;

        if (isGlobalMode) {
          // In global mode, show simplified breadcrumbs
          return [
            {
              label:
                selectionPageData?.[getMainLanguage()]?.pageName ??
                t("cmsTabSelection"),
              to: "/cms/selection",
            },
            {
              label: t("cms.selection.section.artworks.title"),
            },
          ];
        } else {
          // In normal mode, show full breadcrumbs
          return [
            {
              label:
                selectionPageData?.[getMainLanguage()]?.pageName ??
                t("cmsTabSelection"),
              to: "/cms/selection",
            },
            {
              label: sectionData?.content[getMainLanguage()]?.title ?? "",
              to: "/cms/selection/section/" + id,
            },
            {
              label: t("cms.selection.section.artworks.title"),
            },
          ];
        }
      })()}
    >
      <form
        id="sectionArtworks"
        onSubmit={form.handleSubmit(onSubmit, (errors) => {
          toast(
            error({
              description: JSON.stringify(errors),
              duration: 5000,
            }),
          );
        })}
        className="flex flex-col gap-12 w-full px-8 pb-8"
      >
        <ManageLayoutFullMenu control={form.control} />
        <Separator />
        <div className="flex flex-col gap-6">
          <p className="text-lg font-semibold text-gray-950">
            {t("cms.selection.section.artworks.visibility.title")}
          </p>
          <div className="flex flex-col gap-3">
            <p className="text-sm font-medium text-gray-700">
              {t("cms.selection.section.artworks.visibility.subtitle")}
            </p>
            <div className="flex flex-row gap-6 flex-wrap">
              {Object.keys(form.getValues())
                .filter((key) => key !== "layout")
                .map((key) => {
                  return (
                    <VisibilitySwitch
                      key={key}
                      label={t(
                        `cms.selection.section.artworks.visibility.${key}`,
                      )}
                      name={key as keyof SectionDescriptionVisibility}
                      control={form.control}
                    />
                  );
                })}
            </div>
          </div>
        </div>
        <Separator />
        <Tabs defaultValue="selection">
          <TabsList>
            <TabsTrigger value="selection" disabled={artworkSelectionIsDirty}>
              {t("cms.selection.section.artworks.selection")}
            </TabsTrigger>
            <TabsTrigger value="artworks" disabled={artworkSelectionIsDirty}>
              {t("cms.selection.section.artworks.artworks")}
            </TabsTrigger>
            <TabsTrigger
              value="multipleprints"
              disabled={artworkSelectionIsDirty}
            >
              {t("cms.selection.section.artworks.multipleprints")}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="selection">
            <div className="flex flex-col gap-8 pt-6">
              <div className="flex flex-row-reverse">
                <StyledDrawer
                  sheetProps={{
                    open: isImportImageModalOpen,
                    onOpenChange: setIsImportImageModalOpen,
                  }}
                  className="min-w-[30%]"
                  title={t(
                    "cms.selection.section.artworks.importNewImage.title",
                  )}
                  trigger={
                    <Button type="button" variant={"outline"}>
                      {t(
                        "cms.selection.section.artworks.importNewImage.button",
                      )}
                    </Button>
                  }
                >
                  <SectionImportImageForm
                    sectionId={id}
                    sectionName={
                      sectionData?.content[getMainLanguage()]?.title ?? ""
                    }
                    onSuccess={() => {
                      setIsImportImageModalOpen(false);
                    }}
                  />
                </StyledDrawer>
              </div>
              {selectedImages && (
                <SectionSelectedImages
                  selectedImages={selectedImages}
                  sectionId={id}
                  sectionName={
                    sectionData?.content[getMainLanguage()]?.title ?? ""
                  }
                />
              )}
            </div>
          </TabsContent>
          <TabsContent value="artworks">
            {id && selectedImages && (
              <AddSelectionToSection
                onIsDirtyChange={setArtworkSelectionIsDirty}
                sectionId={id}
                selectedImages={selectedImages}
                itemType="artwork"
              />
            )}
          </TabsContent>
          <TabsContent value="multipleprints">
            {id && selectedImages && (
              <AddSelectionToSection
                onIsDirtyChange={setArtworkSelectionIsDirty}
                sectionId={id}
                selectedImages={selectedImages}
                itemType="multipleprint"
              />
            )}
          </TabsContent>
        </Tabs>
      </form>
    </CMSLayout>
  );
};

type VisibilitySwitchProps = {
  label: string;
  name: keyof SectionDescriptionVisibility;
  control: Control<SectionMetadata>;
};

const VisibilitySwitch = ({ label, name, control }: VisibilitySwitchProps) => {
  return (
    <div className="flex flex-col gap-1.5 w-[22%] overflow-hidden">
      <p className="text-sm font-medium text-gray-700">{label}</p>
      <Controller
        key={name}
        control={control}
        name={name}
        render={({ field }) => (
          <Switch
            variant="bigSubtle"
            name={name}
            checked={field.value}
            onCheckedChange={field.onChange}
          />
        )}
      />
    </div>
  );
};

export default SectionArtworks;
